/**
 * Particle point-cloud generators shared by the hero (WebGL) and the
 * business-division stage (2D). Scene space is y-down: positive y renders
 * lower on screen, so "up" is negative y.
 */

/** idea lightbulb: glass shell, glowing nucleus, collar, neck, screw base
 * and rays. DY nudges the whole bulb down so its visual mass aligns with
 * the copy. */
export function makeIdeaBulb(count) {
	const pts = new Float32Array(count * 3);
	const golden = Math.PI * (3 - Math.sqrt(5));
	const DY = 0.12;
	const set = (i, x, y, z) => {
		pts[i * 3] = x;
		pts[i * 3 + 1] = y + DY;
		pts[i * 3 + 2] = z;
	};
	const nGlass = Math.floor(count * 0.4);
	const nCore = Math.floor(count * 0.2);
	const nCollar = Math.floor(count * 0.05);
	const nNeck = Math.floor(count * 0.06);
	const nBase = Math.floor(count * 0.15);
	let i = 0;
	// glass shell — the open bottom cap is trimmed; the collar closes it
	for (let k = 0; k < nGlass; k++, i++) {
		const y = -1 + (k / (nGlass - 1)) * 1.72;
		const r = Math.sqrt(Math.max(0, 1 - y * y));
		const th = golden * k;
		const j = 0.97 + Math.random() * 0.06;
		set(i, Math.cos(th) * r * 0.62 * j, -0.28 + y * 0.62 * j, Math.sin(th) * r * 0.62 * j);
	}
	// energy nucleus, densest at the very center
	for (let k = 0; k < nCore; k++, i++) {
		const r = Math.pow(Math.random(), 1.7) * 0.3;
		const phi = Math.random() * Math.PI * 2;
		const ct = Math.random() * 2 - 1;
		const st = Math.sqrt(1 - ct * ct);
		set(i, Math.cos(phi) * st * r, -0.28 + ct * r, Math.sin(phi) * st * r);
	}
	// collar sealing the glass into the neck (closes the bulb's bottom)
	for (let k = 0; k < nCollar; k++, i++) {
		const t = Math.random();
		const r = 0.43 - t * 0.13 + (Math.random() - 0.5) * 0.02;
		const a = Math.random() * Math.PI * 2;
		set(i, Math.cos(a) * r, 0.166 + t * 0.154, Math.sin(a) * r);
	}
	// neck tapering into the base
	for (let k = 0; k < nNeck; k++, i++) {
		const t = Math.random();
		const r = 0.3 - t * 0.09 + (Math.random() - 0.5) * 0.02;
		const a = Math.random() * Math.PI * 2;
		set(i, Math.cos(a) * r, 0.32 + t * 0.2, Math.sin(a) * r);
	}
	// screw base: three dense thread rings
	for (let k = 0; k < nBase; k++, i++) {
		const a = Math.random() * Math.PI * 2;
		const y = [0.56, 0.65, 0.74][k % 3] + (Math.random() - 0.5) * 0.035;
		const r = 0.22 + (Math.random() - 0.5) * 0.02;
		set(i, Math.cos(a) * r, y, Math.sin(a) * r);
	}
	// rays shining outward, biased to the upper hemisphere
	const RAYS = 9;
	for (; i < count; i++) {
		const ray = (Math.random() * RAYS) | 0;
		const az = (ray / RAYS) * Math.PI * 2 + 0.35;
		const el = -(0.12 + (ray % 3) * 0.32);
		const t = 0.78 + Math.random() * 0.3;
		const ce = Math.cos(el);
		set(i, Math.cos(az) * ce * t, -0.28 + Math.sin(el) * t, Math.sin(az) * ce * t);
	}
	return pts;
}

/** connected globe: shell, graticule (equator, parallels and meridians)
 * and glowing city hubs scattered on the surface */
export function makeGlobe(count) {
	const pts = new Float32Array(count * 3);
	const set = (i, x, y, z) => {
		pts[i * 3] = x;
		pts[i * 3 + 1] = y;
		pts[i * 3 + 2] = z;
	};
	const R = 0.95;
	const golden = Math.PI * (3 - Math.sqrt(5));
	const nShell = Math.floor(count * 0.5);
	const nParallels = Math.floor(count * 0.16);
	const nMeridians = Math.floor(count * 0.16);
	let i = 0;

	// even shell
	for (let k = 0; k < nShell; k++, i++) {
		const y = 1 - (k / (nShell - 1)) * 2;
		const r = Math.sqrt(Math.max(0, 1 - y * y));
		const th = golden * k;
		const jr = 0.99 + Math.random() * 0.03;
		set(i, Math.cos(th) * r * R * jr, y * R * jr, Math.sin(th) * r * R * jr);
	}
	// parallels: equator plus two tropics
	const LATS = [0, -0.62, 0.62];
	for (let k = 0; k < nParallels; k++, i++) {
		const lat = LATS[k % LATS.length];
		const y = lat * R;
		const r = Math.sqrt(Math.max(0, R * R - y * y));
		const a = Math.random() * Math.PI * 2;
		set(i, Math.cos(a) * r, y + (Math.random() - 0.5) * 0.015, Math.sin(a) * r);
	}
	// four meridians through the poles
	for (let k = 0; k < nMeridians; k++, i++) {
		const phi = ((k % 4) / 4) * Math.PI;
		const t = Math.random() * Math.PI * 2;
		set(
			i,
			R * Math.sin(t) * Math.cos(phi),
			R * Math.cos(t),
			R * Math.sin(t) * Math.sin(phi),
		);
	}
	// glowing city hubs: small clusters on the surface
	const HUBS = 18;
	const hubs = Array.from({ length: HUBS }, () => {
		const phi = Math.random() * Math.PI * 2;
		const ct = Math.random() * 2 - 1;
		const st = Math.sqrt(1 - ct * ct);
		return { x: Math.cos(phi) * st, y: ct, z: Math.sin(phi) * st };
	});
	for (; i < count; i++) {
		const h = hubs[(Math.random() * HUBS) | 0];
		const s = 0.045;
		const x = h.x + (Math.random() - 0.5) * s;
		const y = h.y + (Math.random() - 0.5) * s;
		const z = h.z + (Math.random() - 0.5) * s;
		const d = Math.hypot(x, y, z);
		set(i, (x / d) * R * 1.01, (y / d) * R * 1.01, (z / d) * R * 1.01);
	}
	return pts;
}

/** neural network: tiers of glowing nodes stacked top-to-bottom, joined
 * by fine dotted edges. The tier axis is vertical so the layered read
 * survives the yaw spin — the graph stays a graph from every angle.
 * Nodes are tight point clusters (bright cores under additive blending);
 * edges take the remaining budget as pixel-fine links. */
export function makeNeuralNet(count) {
	const pts = new Float32Array(count * 3);
	const set = (i, x, y, z) => {
		pts[i * 3] = x;
		pts[i * 3 + 1] = y;
		pts[i * 3 + 2] = z;
	};
	// y-down scene space: first tier (inputs) on top, output tier below.
	// Few nodes and a nearly-regular ring keep the interior uncluttered;
	// rings wider than the stack is tall, so the graph fills the tile
	// horizontally at every yaw angle
	const TIERS = [
		{ y: -0.72, n: 4, r: 0.62 },
		{ y: -0.25, n: 5, r: 0.78 },
		{ y: 0.25, n: 5, r: 0.78 },
		{ y: 0.72, n: 3, r: 0.46 },
	];
	const tiers = TIERS.map((tier, ti) => {
		const ring = [];
		for (let k = 0; k < tier.n; k++) {
			// stagger tiers so nodes don't align in vertical walls
			const a = (k / tier.n) * Math.PI * 2 + ti * 0.9;
			const r = tier.r * (0.9 + Math.random() * 0.15);
			ring.push({
				x: Math.cos(a) * r + (Math.random() - 0.5) * 0.04,
				y: tier.y + (Math.random() - 0.5) * 0.06,
				z: Math.sin(a) * r + (Math.random() - 0.5) * 0.04,
			});
		}
		return ring;
	});
	// each node links to its 2 nearest nodes of the next tier: short
	// edges hug the shell instead of criss-crossing through the middle
	const edges = [];
	for (let ti = 0; ti < tiers.length - 1; ti++) {
		for (const a of tiers[ti]) {
			const next = [...tiers[ti + 1]].sort(
				(m, n) =>
					Math.hypot(m.x - a.x, m.y - a.y, m.z - a.z) -
					Math.hypot(n.x - a.x, n.y - a.y, n.z - a.z),
			);
			for (let e = 0; e < Math.min(2, next.length); e++) {
				edges.push([a, next[e]]);
			}
		}
	}
	// recenter: with this few nodes the random stagger can lean the graph
	// to one side of the tile
	const nodes = tiers.flat();
	const mx = nodes.reduce((s, n) => s + n.x, 0) / nodes.length;
	const mz = nodes.reduce((s, n) => s + n.z, 0) / nodes.length;
	for (const n of nodes) {
		n.x -= mx;
		n.z -= mz;
	}
	// nodes round-robin so every core gets the same point budget (equal
	// brightness) instead of random picks clumping on a few of them
	const nNodePts = Math.floor(count * 0.24);
	const nTendrilPts = Math.floor(count * 0.16);
	let i = 0;
	for (; i < nNodePts; i++) {
		const n = nodes[i % nodes.length];
		const r = Math.pow(Math.random(), 1.5) * 0.04;
		const phi = Math.random() * Math.PI * 2;
		const ct = Math.random() * 2 - 1;
		const st = Math.sqrt(1 - ct * ct);
		set(
			i,
			n.x + Math.cos(phi) * st * r,
			n.y + ct * r,
			n.z + Math.sin(phi) * st * r,
		);
	}
	// loose terminals: dotted tendrils leaving the outer nodes sideways,
	// toward the rest of the network — they connect to nothing, curve a
	// little and thin out toward the tip, so the graph reads as a live
	// slice of something bigger instead of a closed cage
	const byRadius = [...nodes].sort(
		(m, n) => Math.hypot(n.x, n.z) - Math.hypot(m.x, m.z),
	);
	const N_TENDRILS = 8;
	const tendrils = [];
	for (let k = 0; k < N_TENDRILS; k++) {
		const n = byRadius[k % Math.min(byRadius.length, 10)];
		// mostly radial (outward), with some angular wander
		const a = Math.atan2(n.z, n.x) + (Math.random() - 0.5) * 0.7;
		tendrils.push({
			n,
			dx: Math.cos(a),
			dz: Math.sin(a),
			dy: (Math.random() - 0.5) * 0.5,
			len: 0.28 + Math.random() * 0.18,
			bend: (Math.random() - 0.5) * 0.22,
		});
	}
	const tendrilEnd = i + nTendrilPts;
	const perTendril = nTendrilPts / N_TENDRILS;
	for (let k = 0; k < N_TENDRILS && i < tendrilEnd; k++) {
		const td = tendrils[k];
		const nPts = Math.floor(perTendril * (k + 1)) - Math.floor(perTendril * k);
		for (let p = 0; p < nPts && i < tendrilEnd; p++, i++) {
			// ^1.7 spacing: dense at the node, sparse at the tip → fades out
			const t = Math.pow((p + Math.random() * 0.9) / nPts, 1.7);
			const j = 0.008;
			set(
				i,
				td.n.x + td.dx * td.len * t + (Math.random() - 0.5) * j,
				td.n.y +
					td.dy * td.len * t +
					td.bend * t * t +
					(Math.random() - 0.5) * j,
				td.n.z + td.dz * td.len * t + (Math.random() - 0.5) * j,
			);
		}
	}
	// edges: stratified sampling — evenly spaced dots with a little slack,
	// so the links read as fine dotted lines instead of clumpy streaks
	const nEdgePts = count - i;
	const perEdge = nEdgePts / edges.length;
	for (let e = 0; e < edges.length && i < count; e++) {
		const [a, b] = edges[e];
		const nPts = Math.floor(perEdge * (e + 1)) - Math.floor(perEdge * e);
		for (let p = 0; p < nPts && i < count; p++, i++) {
			const t = (p + 0.15 + Math.random() * 0.7) / nPts;
			const j = 0.008;
			set(
				i,
				a.x + (b.x - a.x) * t + (Math.random() - 0.5) * j,
				a.y + (b.y - a.y) * t + (Math.random() - 0.5) * j,
				a.z + (b.z - a.z) * t + (Math.random() - 0.5) * j,
			);
		}
	}
	// leftover slots from the floor rounding above go to random edges
	for (; i < count; i++) {
		const [a, b] = edges[(Math.random() * edges.length) | 0];
		const t = Math.random();
		set(
			i,
			a.x + (b.x - a.x) * t,
			a.y + (b.y - a.y) * t,
			a.z + (b.z - a.z) * t,
		);
	}
	return pts;
}

/** brain: what sells the shape are the gyri — serpentine cortical fold
 * bands sampled on the surface — plus the midline fissure and a flatter
 * base. Points off the fold ridges are rejected, so the surface reads as
 * coiled tissue instead of a noisy ball. */
export function makeBrain(count) {
	const pts = new Float32Array(count * 3);
	const set = (i, x, y, z) => {
		pts[i * 3] = x;
		pts[i * 3 + 1] = y;
		pts[i * 3 + 2] = z;
	};
	const nCerebellum = Math.floor(count * 0.2);
	const nSurface = count - nCerebellum;
	let i = 0;
	let guard = 0;

	// per-sculpture fold phases: no two brains coil exactly the same way
	const q1 = Math.random() * Math.PI * 2;
	const q2 = Math.random() * Math.PI * 2;
	const q3 = Math.random() * Math.PI * 2;

	while (i < nSurface && guard < count * 50) {
		guard++;
		const phi = Math.random() * Math.PI * 2;
		const ct = Math.random() * 2 - 1;
		const st = Math.sqrt(1 - ct * ct);
		const dx = Math.cos(phi) * st;
		const dy = ct;
		const dz = Math.sin(phi) * st;
		// interhemispheric fissure
		if (Math.abs(dx) < 0.08) continue;
		// keep only the winding gyri ridges: two warped sine octaves, so
		// the coarse serpentine coils are broken up by a finer meander and
		// the ridges wander instead of striping evenly
		const theta = Math.atan2(dz, dx);
		const lat = Math.asin(dy);
		const warp =
			Math.sin(lat * 3 + q2) * 2.2 +
			Math.sin(theta * 2.3 + lat * 1.7 + q3) * 1.1;
		const fold =
			0.62 * Math.sin(theta * 6 + warp + q1) +
			0.38 * Math.sin(theta * 10.7 - lat * 4.3 + Math.sin(theta * 3.1 + q2) * 1.4);
		// ragged band edges plus a light dusting of stray specks off the
		// ridges, so the pattern reads organic instead of machine-striped
		const edge = 0.52 + (Math.random() - 0.5) * 0.3;
		if (Math.abs(fold) > edge && Math.random() > 0.06) continue;
		// carve the back-bottom notch where the cerebellum tucks in
		if (dz > 0.45 && dy > 0.25) continue;
		// ellipsoid envelope with a flattened base (y-down = bottom)
		let ey = dy * 0.75;
		if (ey > 0.42) ey = 0.42 + (ey - 0.42) * 0.45;
		const relief =
			1 + 0.035 * (1 - Math.abs(fold)) + (Math.random() - 0.5) * 0.04;
		set(i, dx * relief, ey * relief, dz * 0.86 * relief);
		i++;
	}
	// cerebellum: the smaller, finely-striped lobe at the back-bottom —
	// the strongest "this is a brain" cue. Only its outer face is drawn:
	// samples buried inside the cerebrum envelope would render as loose
	// pixels floating in the silhouette and muddy the read.
	guard = 0;
	while (i < count && guard < count * 50) {
		guard++;
		const phi = Math.random() * Math.PI * 2;
		const ct = Math.random() * 2 - 1;
		const st = Math.sqrt(1 - ct * ct);
		const dx = Math.cos(phi) * st;
		const dy = ct;
		const dz = Math.sin(phi) * st;
		// folia: tight horizontal striations, gently wobbled so they don't
		// read as machine-ruled lines
		const folia = Math.sin(dy * 9 + Math.sin(dx * 5.3 + q1) * 0.9);
		if (Math.abs(folia) > 0.62 + (Math.random() - 0.5) * 0.22) continue;
		const px = dx * 0.46 + (Math.random() - 0.5) * 0.015;
		const py = 0.44 + dy * 0.26;
		const pz = 0.52 + dz * 0.34;
		// inside the cerebrum ellipsoid → hidden face, skip
		if (px * px + (py / 0.75) ** 2 + (pz / 0.86) ** 2 < 1) continue;
		set(i, px, py, pz);
		i++;
	}
	return pts;
}

/** friendly robot head: rounded shell, glowing eyes, speaker grill,
 * side ears and an antenna */
export function makeRobot(count) {
	const pts = new Float32Array(count * 3);
	const set = (i, x, y, z) => {
		pts[i * 3] = x;
		pts[i * 3 + 1] = y;
		pts[i * 3 + 2] = z;
	};
	const SX = 0.92;
	const SY = 0.74;
	const SZ = 0.74;
	const HEAD_Y = 0.05; // head center, leaving room for the antenna above
	const nShell = Math.floor(count * 0.52);
	const nEyes = Math.floor(count * 0.14);
	const nGrill = Math.floor(count * 0.12);
	const nEars = Math.floor(count * 0.08);
	let i = 0;

	// rounded-cube shell (superquadric)
	for (let k = 0; k < nShell; k++, i++) {
		const phi = Math.random() * Math.PI * 2;
		const ct = Math.random() * 2 - 1;
		const st = Math.sqrt(1 - ct * ct);
		const dx = Math.cos(phi) * st;
		const dy = ct;
		const dz = Math.sin(phi) * st;
		const m = Math.pow(
			Math.abs(dx) ** 4 + Math.abs(dy) ** 4 + Math.abs(dz) ** 4,
			1 / 4,
		);
		const j = 0.99 + Math.random() * 0.03;
		set(
			i,
			(dx / m) * SX * j,
			HEAD_Y + (dy / m) * SY * j,
			(dz / m) * SZ * j,
		);
	}
	// glowing eyes on the front face
	for (let k = 0; k < nEyes; k++, i++) {
		const side = k % 2 === 0 ? -1 : 1;
		const a = Math.random() * Math.PI * 2;
		const r = Math.sqrt(Math.random()) * 0.15;
		set(
			i,
			side * 0.34 + Math.cos(a) * r,
			HEAD_Y - 0.16 + Math.sin(a) * r,
			-SZ * (0.99 + Math.random() * 0.04),
		);
	}
	// speaker grill under the eyes
	for (let k = 0; k < nGrill; k++, i++) {
		const row = k % 3;
		set(
			i,
			(Math.random() - 0.5) * 0.56,
			HEAD_Y + 0.3 + row * 0.09 + (Math.random() - 0.5) * 0.02,
			-SZ * (0.99 + Math.random() * 0.04),
		);
	}
	// side ears
	for (let k = 0; k < nEars; k++, i++) {
		const side = k % 2 === 0 ? -1 : 1;
		const a = Math.random() * Math.PI * 2;
		set(
			i,
			side * (SX + 0.02 + Math.random() * 0.09),
			HEAD_Y - 0.05 + Math.cos(a) * 0.16,
			Math.sin(a) * 0.16,
		);
	}
	// antenna: stem + tip
	for (; i < count; i++) {
		if (i % 3 === 0) {
			// glowing tip
			const phi = Math.random() * Math.PI * 2;
			const ct = Math.random() * 2 - 1;
			const st = Math.sqrt(1 - ct * ct);
			const r = 0.1 * (0.9 + Math.random() * 0.2);
			set(
				i,
				Math.cos(phi) * st * r,
				HEAD_Y - SY - 0.36 + ct * r,
				Math.sin(phi) * st * r,
			);
		} else {
			const t = Math.random();
			set(
				i,
				(Math.random() - 0.5) * 0.035,
				HEAD_Y - SY - t * 0.28,
				(Math.random() - 0.5) * 0.035,
			);
		}
	}
	return pts;
}

/** retro rocket pointing up: nose cone, hull, porthole ring, three fins
 * and a nozzle — sized to pair with the stage's thruster exhaust below */
export function makeRocket(count) {
	const pts = new Float32Array(count * 3);
	const set = (i, x, y, z) => {
		pts[i * 3] = x;
		pts[i * 3 + 1] = y;
		pts[i * 3 + 2] = z;
	};
	const nNose = Math.floor(count * 0.18);
	const nHull = Math.floor(count * 0.32);
	const nPorthole = Math.floor(count * 0.08);
	const nRings = Math.floor(count * 0.1);
	const nFins = Math.floor(count * 0.24);
	let i = 0;
	const j = () => (Math.random() - 0.5) * 0.02;

	// nose cone: tip at -1.0 opening to the hull radius
	for (let k = 0; k < nNose; k++, i++) {
		const t = Math.sqrt(Math.random()); // denser toward the wide end
		const r = t * 0.3;
		const a = Math.random() * Math.PI * 2;
		set(i, Math.cos(a) * r + j(), -1 + t * 0.55 + j(), Math.sin(a) * r + j());
	}
	// hull cylinder
	for (let k = 0; k < nHull; k++, i++) {
		const a = Math.random() * Math.PI * 2;
		const y = -0.45 + Math.random() * 0.95;
		set(i, Math.cos(a) * 0.3 + j(), y + j(), Math.sin(a) * 0.3 + j());
	}
	// porthole: bright band around the upper hull
	for (let k = 0; k < nPorthole; k++, i++) {
		const a = Math.random() * Math.PI * 2;
		set(i, Math.cos(a) * 0.315, -0.18 + (Math.random() - 0.5) * 0.05, Math.sin(a) * 0.315);
	}
	// silhouette rings at the panel joints
	for (let k = 0; k < nRings; k++, i++) {
		const a = Math.random() * Math.PI * 2;
		const y = k % 2 === 0 ? -0.45 : 0.5;
		set(i, Math.cos(a) * 0.305, y + (Math.random() - 0.5) * 0.02, Math.sin(a) * 0.305);
	}
	// three fins sweeping out from the lower hull
	for (let k = 0; k < nFins; k++, i++) {
		const fin = k % 3;
		const baseA = (fin / 3) * Math.PI * 2 + 0.5;
		const t = Math.random(); // 0 = fin top, 1 = fin bottom
		const y = 0.12 + t * 0.52;
		const maxR = 0.3 + t * 0.35;
		const r = 0.3 + Math.random() * (maxR - 0.3);
		const a = baseA + (Math.random() - 0.5) * 0.04;
		set(i, Math.cos(a) * r, y + j(), Math.sin(a) * r);
	}
	// nozzle taper
	for (; i < count; i++) {
		const t = Math.random();
		const r = 0.26 - t * 0.09;
		const a = Math.random() * Math.PI * 2;
		set(i, Math.cos(a) * r + j(), 0.5 + t * 0.17 + j(), Math.sin(a) * r + j());
	}
	return pts;
}
